import React, { useCallback, useEffect, useState } from 'react';

import rapidFire from '../../../main/assets/images/rapidFire/rapid-fire-visualisation.webp';
import rapidFireTablet from '../../../main/assets/images/rapidFire/rapid-fire-visualisation-tablet.webp';
import rapidFireProduct from '../../../main/assets/images/rapidFire/rapid-fire-design-details.webp';

const objectPostitions = {
  leftSide: {
    linePosition: {
      x: 135,
      y: 490,
    },
    titlePosition: {
      x: 8,
      y: 52,
    },
    descriptionPosition: {
      x: 8,
      y: 64,
    },
  },
  rightSide: {
    linePosition: {
      x: 900,
      y: 320,
    },
    titlePosition: {
      x: 62,
      y: 35,
    },
    descriptionPosition: {
      x: 69,
      y: 47,
    },
  },
  tablet: {
    leftSide: {
      linePosition: {
        x: 75,
        y: 475,
      },
      titlePosition: {
        x: 6,
        y: 64,
      },
      descriptionPosition: {
        x: 6,
        y: 75,
      },
    },
    rightSide: {
      linePosition: {
        x: 670,
        y: 215,
      },
      titlePosition: {
        x: 50,
        y: 31,
      },
      descriptionPosition: {
        x: 71,
        y: 42.5,
      },
    },
  },
  mobile: {
    imageContainer: {
      width: 70,
      height: 70,
      top: 50,
      left: 50,
      transform: 'translate(-50%, -50%)',
    },
    leftSection: {
      width: '100%',
    },
    rightSection: {
      width: '100%',
    },
  },
  wideScreen: {
    leftSide: {
      linePosition: {
        x: 85,
        y: 490,
      },
      titlePosition: {
        x: 5.5,
        y: 52,
      },
      descriptionPosition: {
        x: 5.5,
        y: 62,
      },
    },
    rightSide: {
      linePosition: {
        x: 950,
        y: 320,
      },
      titlePosition: {
        x: 64,
        y: 35,
      },
      descriptionPosition: {
        x: 71,
        y: 45,
      },
    },
  },
};

const BREAKPOINT_WIDE_SCREEN = 2400;
const BREAKPOINT_DESKTOP = 1201;
const BREAKPOINT_TABLET = 991;

export const Visualisation = () => {
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.target as HTMLImageElement;
      setImageSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    },
    [],
  );

  const getCoordinates = useCallback(
    (xPercent: number, yPercent: number) => {
      if (imageSize.width === 0 || imageSize.height === 0) {
        return { x: 0, y: 0 };
      }
      return {
        x: (xPercent / 100) * imageSize.width,
        y: (yPercent / 100) * imageSize.height,
      };
    },
    [imageSize],
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= BREAKPOINT_TABLET;
  const isTablet =
    windowWidth < BREAKPOINT_DESKTOP && windowWidth > BREAKPOINT_TABLET;
  const isWideScreen = windowWidth > BREAKPOINT_WIDE_SCREEN;

  const currentPositions = isTablet
    ? objectPostitions.tablet
    : isWideScreen
    ? objectPostitions.wideScreen
    : objectPostitions;

  return (
    <div
      className="rapid-fire-visual"
      style={{
        position: 'relative',
        maxHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '20px' : '0',
      }}
    >
      {isMobile ? (
        <>
          {/* Mobile Left Section (Top) */}
          <div
            style={{
              width: objectPostitions.mobile.leftSection.width,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              textAlign: 'right',
            }}
            className="rapid-fire-visual-left"
          >
            <p
              style={{
                color: 'white',
                textTransform: 'uppercase',
                marginBottom: '20px',
                width: '50%',
                lineHeight: 'normal',
              }}
              className="rapid-visual-left-title"
            >
              User-Friendly Interface
            </p>
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.70)',
                width: '50%',
                lineHeight: '140%',
              }}
              className={'rapid-visual-left-description'}
            >
              Navigating settings is a breeze with the OLED display and 5-way
              joystick. The intuitive interface provides OSD feedback, keeping
              you informed on critical parameters for a smooth operation.
            </p>
          </div>

          {/* Mobile Image (Center) */}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={rapidFireProduct}
              alt="User Interface and Integrated Tools of the Device"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              className="rapid-visual-center-image"
              onLoad={handleImageLoad}
            />
          </div>

          {/* Mobile Right Section (Bottom) */}
          <div
            className="rapid-fire-visual-right"
            style={{
              width: objectPostitions.mobile.rightSection.width,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <p
              style={{
                color: 'white',
                textTransform: 'uppercase',
                marginBottom: '20px',
                width: '70%',
                lineHeight: 'normal',
              }}
              className="rapid-visual-right-title"
            >
              Powerful Built-in Tools
            </p>
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.70)',
                width: '50%',
                lineHeight: '140%',
              }}
              className="rapid-visual-right-description"
            >
              Packed with features like a spectrum analyzer, favorite channels,
              live single-channel spectrum display, and band scanning, the
              rapidFIRE ensures you have all the tools necessary for an optimal
              flying experience.
            </p>
          </div>
        </>
      ) : (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <img
            src={isTablet ? rapidFireTablet : rapidFire}
            alt="User Interface and Integrated Tools of the Device"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
            }}
            onLoad={handleImageLoad}
          />
          {imageSize.width > 0 && (
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
              viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Left Side Path */}
              <g
                transform={`translate(${currentPositions.leftSide.linePosition.x}, ${currentPositions.leftSide.linePosition.y})`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="802"
                  height="12"
                  viewBox="0 0 802 12"
                  fill="none"
                >
                  <path
                    d={
                      isTablet
                        ? 'M435.333 6C435.333 3.05448 432.946 0.666667 430 0.666667C427.054 0.666666 424.667 3.05448 424.667 6C424.667 8.94552 427.054 11.3333 430 11.3333C432.946 11.3333 435.333 8.94552 435.333 6ZM430 5L8.74228e-08 4.99996L-8.74228e-08 6.99996L430 7L430 5Z'
                        : 'M801.333 6C801.333 3.05448 798.946 0.666667 796 0.666667C793.054 0.666666 790.667 3.05448 790.667 6C790.667 8.94552 793.054 11.3333 796 11.3333C798.946 11.3333 801.333 8.94552 801.333 6ZM796 5L8.74228e-08 4.99993L-8.74228e-08 6.99993L796 7L796 5Z'
                    }
                    fill="url(#paint0_linear_1053_2685)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_1053_2685"
                      x1="796"
                      y1="5.5"
                      x2="4.37113e-08"
                      y2="5.49993"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0.509" stopColor="#FF2238" />
                      <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </g>

              {/* Left Side Title */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.leftSide.titlePosition.x,
                    currentPositions.leftSide.titlePosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.leftSide.titlePosition.x,
                    currentPositions.leftSide.titlePosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '30%' : '25%'}
                  height="100%"
                  style={{ pointerEvents: 'none' }}
                >
                  <p
                    style={{
                      fontSize: isTablet || isWideScreen ? '32px' : '42px',
                      textTransform: 'uppercase',
                      lineHeight: 'normal',
                    }}
                  >
                    Powerful Built-in Tools
                  </p>
                </foreignObject>
              </g>

              {/* Left Side Description */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.leftSide.descriptionPosition.x,
                    currentPositions.leftSide.descriptionPosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.leftSide.descriptionPosition.x,
                    currentPositions.leftSide.descriptionPosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '30%' : '20%'}
                  height="50%"
                  style={{ pointerEvents: 'none' }}
                >
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.70)',
                      fontSize: isTablet || isWideScreen ? '16px' : '18px',
                      lineHeight: '140%',
                    }}
                  >
                    Packed with features like a spectrum analyzer, favorite
                    channels, live single-channel spectrum display, and band
                    scanning, the rapidFIRE ensures you have all the tools
                    necessary for an optimal flying experience.
                  </p>
                </foreignObject>
              </g>

              {/* Right Side Path */}
              <g
                transform={`translate(${currentPositions.rightSide.linePosition.x}, ${currentPositions.rightSide.linePosition.y})`}
              >
                {isTablet ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="468"
                    height="12"
                    viewBox="0 0 468 12"
                    fill="none"
                  >
                    <path
                      d="M0.666667 6.00012C0.666667 8.94564 3.05448 11.3335 6 11.3335C8.94552 11.3335 11.3333 8.94564 11.3333 6.00012C11.3333 3.0546 8.94552 0.666788 6 0.666789C3.05448 0.666789 0.666666 3.0546 0.666667 6.00012ZM6 6.00012L6 7.00012L468 7.00004L468 6.00004L468 5.00004L6 5.00012L6 6.00012Z"
                      fill="url(#paint0_linear_1053_2779)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_1053_2779"
                        x1="6"
                        y1="6.50012"
                        x2="468"
                        y2="6.50004"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0.509" stopColor="#FF2238" />
                        <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="867"
                    height="12"
                    viewBox="0 0 867 12"
                    fill="none"
                  >
                    <path
                      d="M0.666667 6.00025C0.666667 8.94576 3.05448 11.3336 6 11.3336C8.94552 11.3336 11.3333 8.94576 11.3333 6.00024C11.3333 3.05472 8.94552 0.66691 6 0.666911C3.05448 0.666911 0.666666 3.05473 0.666667 6.00025ZM6 6.00024L6 7.00024L867 7.00009L867 6.00009L867 5.00009L6 5.00024L6 6.00024Z"
                      fill="url(#paint0_linear_1053_2691)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_1053_2691"
                        x1="6"
                        y1="6.50024"
                        x2="867"
                        y2="6.50009"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0.509" stopColor="#FF2238" />
                        <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}
              </g>

              {/* Right Side Title */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.rightSide.titlePosition.x,
                    currentPositions.rightSide.titlePosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.rightSide.titlePosition.x,
                    currentPositions.rightSide.titlePosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '45%' : '30%'}
                  height="50%"
                  style={{
                    pointerEvents: 'none',
                    textTransform: 'uppercase',
                    textAlign: 'right',
                  }}
                >
                  <p
                    style={{
                      color: 'white',
                      fontSize: isTablet || isWideScreen ? '32px' : '42px',
                      lineHeight: 'normal',
                    }}
                  >
                    User-Friendly Interface
                  </p>
                </foreignObject>
              </g>

              {/* Right Side Description */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.rightSide.descriptionPosition.x,
                    currentPositions.rightSide.descriptionPosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.rightSide.descriptionPosition.x,
                    currentPositions.rightSide.descriptionPosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '24%' : '23%'}
                  height="50%"
                  style={{ pointerEvents: 'none' }}
                >
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.70)',
                      fontSize: isTablet || isWideScreen ? '16px' : '18px',
                      textAlign: 'right',
                      lineHeight: '140%',
                    }}
                  >
                    Navigating settings is a breeze with the OLED display and
                    5-way joystick. The intuitive interface provides OSD
                    feedback, keeping you informed on critical parameters for a
                    smooth operation.
                  </p>
                </foreignObject>
              </g>
            </svg>
          )}
        </div>
      )}
    </div>
  );
};
